import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { number, object, string, array } from "yup";
import CancelIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import dayjs from "dayjs";
import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Modal,
  Select,
  TextField,
  Typography,
  MenuItem,
} from "@mui/material";
import useAxiosPrivate from "contexts/interceptors/axios";
import { useStateContext } from "../../contexts/ContextProvider";
import SelectBulkRecipients from "../../components/i-abide/Textfields/SelectBulkRecepients";
import SelectDirectory from "../../components/i-abide/Textfields/SelectDirectory";
import SelectSubmissionTypes from "../../components/i-abide/Textfields/SelectSubmissionType";
import SelectSpecificRecipients from "../../components/i-abide/Textfields/SelectRecipients";

const style = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  position: "absolute",
  backgroundColor: "#e9e9e9",
  boxShadow: "3px 2px 20px 3px rgba(0, 0, 0, 0.4)",
  borderRadius: "10px",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  overflow: "auto",
  "@media (min-width: 10px)": {
    height: "90vh",
    width: "85vw",
  },
  "@media (min-width: 480px)": {
    height: "85vh",
    width: "80vw",
  },
  "@media (min-width: 640px)": {
    height: "75vh",
    width: "70vw",
  },
  "@media (min-width: 768px)": {
    height: "75vh",
    width: "70vw",
  },
  "@media (min-width: 1024px)": {
    height: "80vh",
    width: "70vw",
  },
  "@media (min-width: 1082px)": {
    height: "80vh",
    width: "65vw",
  },
  "@media (min-width: 1440px)": {
    height: "75vh",
    width: "60vw",
  },
  "@media (min-width: 2560px)": {
    height: "55vh",
    width: "50vw", // Adjust this as necessary for large screens
  },
  "@media (min-width: 2380px)": {
    height: "55vh",
    width: "50vw", // Adjust this as necessary for large screens
  },
};

export default function CreatePostModal({ open, handleClose }) {
  const { auth } = useStateContext();
  const axiosPrivate = useAxiosPrivate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [shareTo, setShareTo] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  // const [dropdowns, setDropdowns] = useState([0]);
  const [subdirectories, setSubdirectories] = useState({}); // State for subdirectories
  const [officeName, setOfficeName] = useState("");
  const [selectedRecipientType, setSelectedRecipientType] = useState("");

  const formik = useFormik({
    initialValues: {
      post_title: "",
      instructions: "",
      activity_Program: "",
      link_to_memo: "",
      title_of_memo: "",
      posting_date: new Date(
        new Date().getTime() - new Date().getTimezoneOffset() * 60000
      )
        .toISOString()
        .slice(0, 16),
      role: auth?.role,
      deadline: "",
      posted_by: auth?.username,
      officeId: auth?.officeId,
      directoryId: "",
      subDirectoryId: "",
      submissionTypeId: null,
      linkProvided: "",
      selectedRecipients: [],
      shareTo: [],
    },

    validationSchema: object().shape({
      post_title: string().required("Required"),
      instructions: string().required("Required"),
      activity_Program: string().required("Required"),
      link_to_memo: string().required("Required"),
      title_of_memo: string().required("Required"),
      posting_date: string().required("Required"),
      role: string().required("Required"),
      deadline: string().required("Required"),
      posted_by: string().required("Required"),
      officeId: number().nullable(),
      directoryId: number().required("Required"),
      subDirectoryId: string().nullable(),
      submissionTypeId: number().required("Required"),
      linkProvided: string(),
      selectedRecipients: array().required("Required"),
      shareTo: array().required("Required"),
    }),
    onSubmit: (values) => {
      // Log the form values right before formatting and submission

      // Indicate that the submission process is starting
      setLoading(true);
      setError("");

      // Modify date formats and other values as needed before submission
      const formattedValues = {
        ...values,
        posting_date: dayjs(values.posting_date).format("MMMM D, YYYY h:mm A"),
        deadline: dayjs(values.deadline).format("MMMM D, YYYY h:mm A"),
        shareTo, // Use the actual `shareTo` state
        selectedRecipients,
      };

      // Log the final formatted values to inspect the request body structure
      console.log("Final Request Body Structure:", formattedValues);

      // Proceed with submission
      axiosPrivate
        .post(`/I-Abide/createPost`, formattedValues)
        .then(() => {
          alert("Successfully added your post");
          formik.resetForm();
          setShareTo([]); // Reset the shareTo selections
          setSelectedRecipients([]);
          handleClose();
        })
        .catch((err) => {
          setError(err?.message);
        })
        .finally(() => {
          setLoading(false);
        });
    },
  });

  // Fetch subdirectories when directoryId changes
  useEffect(() => {
    if (formik.values.directoryId) {
      axiosPrivate
        .get(`/I-Abide/getSubdirectories/${formik.values.directoryId}`)
        .then((res) => {
          setSubdirectories(res?.data); // Set the fetched subdirectories
        })
        .catch((err) => {
          console.error("Failed to fetch subdirectories:", err);
        });
    } else {
      setSubdirectories([]); // Clear subdirectories if no directoryId
    }
  }, [formik.values.directoryId]);

  const handleAddDropdown = () => {
    // setDropdowns((prev) => [...prev, prev.length]); // Add a new dropdown identifier
    setShareTo((prev) => [...prev, {}]); // Add an empty object for new dropdown data
    setSelectedRecipients((prev) => [...prev, {}]);
  };

  const handleDropdownChange = (index, recipient) => {
    const updatedShareTo = [...shareTo];
    updatedShareTo[index] = { ...recipient };
    setShareTo(updatedShareTo);

    // Update Formik state
    formik.setFieldValue("shareTo", updatedShareTo);
  };

  const handleSelectedRecipientsChange = (index, selectedRecipient) => {
    const updatedselectedRecipient = [...selectedRecipients];
    updatedselectedRecipient[index] = selectedRecipient.id;
    setSelectedRecipients(updatedselectedRecipient);

    // Update Formik state
    formik.setFieldValue("selectedRecipients", updatedselectedRecipient);
  };

  // Fetch office name using officeId
  useEffect(() => {
    if (auth?.officeId) {
      axiosPrivate
        .get(`/I-Abide/getOfficeById/${auth?.officeId}`)
        .then((res) => {
          setOfficeName(res?.data?.officeName || ""); // Assume the office name is returned in the `name` field
        })
        .catch((err) => {
          console.error("Error fetching office name:", err);
          setOfficeName("Unknown Office"); // Fallback if the name can't be fetched
        });
    }
  }, [auth?.officeId]);

  const handleRecipientTypeChange = (event) => {
    setSelectedRecipientType(event.target.value);
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        variant="form"
        component="form"
        onSubmit={formik.handleSubmit}
        autoComplete="off"
        sx={style}
      >
        <Box
          sx={{
            backgroundColor: "#2a9618",
            width: "100%",
            p: 3, // Remove horizontal padding
            mb: 2,
          }}
        >
          <Typography
            sx={{ ml: 2, fontSize: 18, fontWeight: "bold", color: "#fff" }}
          >
            CREATE POST
          </Typography>
        </Box>
        <Box
          sx={{
            position: "absolute",
            right: 10,
            cursor: "pointer",
            zIndex: 100,
            pt: 1,
          }}
        >
          <IconButton onClick={handleClose} sx={{ p: 0 }}>
            <CancelIcon />
          </IconButton>
        </Box>

        <Box>
          {error}
          <Box
            sx={{
              backgroundColor: "rgba(255, 255, 255, 1)",
              boxShadow: "8px 8px 15px 3px rgba(0, 0, 0, 0.3)",
              borderRadius: "10px",
              p: 4,
              mx: 4,
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  id="post_title"
                  label="Title of Requirement(s)"
                  variant="outlined"
                  size="small"
                  disabled={loading}
                  value={formik.values.post_title}
                  onChange={(event) => {
                    formik.handleChange(event);
                    console.log("Post Title:", event.target.value); // Log the value
                  }}
                  onBlur={formik.handleBLur}
                  error={
                    formik.touched.post_title &&
                    Boolean(formik.errors.post_title)
                  }
                  helperText={
                    formik.touched.post_title && formik.errors.post_title
                  }
                  fullWidth
                  multiline
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="instructions"
                  label="Instructions"
                  size="small"
                  disabled={loading}
                  value={formik.values.instructions}
                  onChange={(event) => {
                    formik.handleChange(event);
                    console.log("Instructions:", event.target.value); // Log the value
                  }}
                  onBlur={formik.handleBLur}
                  error={
                    formik.touched.instructions &&
                    Boolean(formik.errors.instructions)
                  }
                  helperText={
                    formik.touched.instructions && formik.errors.instructions
                  }
                  variant="outlined"
                  fullWidth
                  multiline
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="activity_Program"
                  label="Activity/Program/Project"
                  variant="outlined"
                  size="small"
                  disabled={loading}
                  value={formik.values.activity_Program}
                  onChange={(event) => {
                    formik.handleChange(event);
                    console.log(
                      "Activity/Program/Project:",
                      event.target.value
                    ); // Log the value
                  }}
                  onBlur={formik.handleBLur}
                  error={
                    formik.touched.activity_Program &&
                    Boolean(formik.errors.activity_Program)
                  }
                  helperText={
                    formik.touched.activity_Program &&
                    formik.errors.activity_Program
                  }
                  fullWidth
                  multiline
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="link_to_memo"
                  label="Link to Memo"
                  variant="outlined"
                  size="small"
                  disabled={loading}
                  value={formik.values.link_to_memo}
                  onChange={(event) => {
                    formik.handleChange(event);
                    console.log("Link to Memo", event.target.value); // Log the value
                  }}
                  onBlur={formik.handleBLur}
                  error={
                    formik.touched.link_to_memo &&
                    Boolean(formik.errors.link_to_memo)
                  }
                  helperText={
                    formik.touched.link_to_memo && formik.errors.link_to_memo
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="title_of_memo"
                  label="Title of Memo"
                  variant="outlined"
                  size="small"
                  disabled={loading}
                  value={formik.values.title_of_memo}
                  onChange={(event) => {
                    formik.handleChange(event);
                    console.log("Title of Memo", event.target.value); // Log the value
                  }}
                  onBlur={formik.handleBLur}
                  error={
                    formik.touched.title_of_memo &&
                    Boolean(formik.errors.title_of_memo)
                  }
                  helperText={
                    formik.touched.title_of_memo && formik.errors.title_of_memo
                  }
                  fullWidth
                  multiline
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  id="posted_by"
                  label="Posted By"
                  variant="outlined"
                  size="small"
                  disabled={loading}
                  value={formik.values.posted_by}
                  onChange={(event) => {
                    formik.handleChange(event);
                    console.log("Posted By", event.target.value); // Log the value
                  }}
                  onBlur={formik.handleBLur}
                  error={
                    formik.touched.posted_by && Boolean(formik.errors.posted_by)
                  }
                  helperText={
                    formik.touched.posted_by && formik.errors.posted_by
                  }
                  sx={{ pr: 5 }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  id="role"
                  label="Role"
                  variant="outlined"
                  size="small"
                  disabled={loading}
                  value={formik.values.role}
                  onChange={(event) => {
                    formik.handleChange(event);
                    console.log("Role", event.target.value); // Log the value
                  }}
                  onBlur={formik.handleBLur}
                  error={formik.touched.role && Boolean(formik.errors.role)}
                  helperText={formik.touched.role && formik.errors.role}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="officeId"
                  label="Office"
                  variant="outlined"
                  size="small"
                  disabled={loading}
                  value={officeName}
                  onChange={(event) => {
                    formik.handleChange(event);
                    console.log("Office", event.target.value); // Log the value
                  }}
                  onBlur={formik.handleBLur}
                  error={
                    formik.touched.officeId && Boolean(formik.errors.officeId)
                  }
                  helperText={formik.touched.officeId && formik.errors.officeId}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <Typography>Posting Date</Typography>
                <TextField
                  id="posting_date"
                  // label="Posting Date"
                  variant="outlined"
                  size="small"
                  type="datetime-local"
                  disabled={loading}
                  value={formik.values.posting_date}
                  onChange={(event) => {
                    formik.handleChange(event);
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.posting_date &&
                    Boolean(formik.errors.posting_date)
                  }
                  helperText={
                    formik.touched.posting_date && formik.errors.posting_date
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <Typography>Deadline</Typography>
                <TextField
                  id="deadline"
                  // label="Deadline"
                  variant="outlined"
                  size="small"
                  type="datetime-local"
                  disabled={loading}
                  value={formik.values.deadline}
                  onChange={(event) => {
                    formik.handleChange(event);
                    console.log("Deadline", event.target.value); // Log the value
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.deadline && Boolean(formik.errors.deadline)
                  }
                  helperText={formik.touched.deadline && formik.errors.deadline}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <SelectDirectory
                  label={`Directory ${formik.values.directoryId ? "" : "*"}`}
                  name="directoryId"
                  width="100%"
                  // disabled={loading}
                  value={formik.values.directoryId}
                  onChange={(fieldName, selectedValue) => {
                    console.log("Selected Directory ID:", selectedValue);

                    formik.setFieldValue("directoryId", selectedValue);

                    axiosPrivate
                      .get(`/I-Abide/getSubdirectories/${selectedValue}`)
                      .then((res) => {
                        setSubdirectories(res?.data);
                        formik.setFieldValue("subDirectoryId", "");
                      })
                      .catch((err) => {
                        console.error("Failed to fetch subdirectories:", err);
                        setSubdirectories([]);
                      });
                  }}
                  onBlur={formik.handleBLur}
                  error={
                    formik.touched.directoryId &&
                    Boolean(formik.errors.directoryId)
                  }
                  helperText={
                    formik.touched.directoryId && formik.errors.directoryId
                  }
                  sx={{
                    width: "100%",
                    "&:hover": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "black !important",
                      },
                    },
                  }}
                />
              </Grid>

              {/* Subdirectory Dropdown */}
              <Grid item xs={6}>
                <Select
                  name="subDirectoryId"
                  value={formik?.values?.subDirectoryId}
                  onChange={(e) => {
                    const selectedSubdirectoryId = e.target.value; // Capture selected ID
                    console.log(
                      "Selected Subdirectory ID:",
                      selectedSubdirectoryId
                    ); // Log selected value
                    formik.setFieldValue(
                      "subDirectoryId",
                      selectedSubdirectoryId
                    ); // Set the selected value to Formik
                  }}
                  onBlur={formik?.handleBlur}
                  size="small"
                  disabled={!subdirectories?.data?.length || loading}
                  error={
                    formik?.touched?.subDirectoryId &&
                    Boolean(formik?.errors?.subDirectoryId)
                  }
                  helperText={
                    formik.touched.subDirectoryId &&
                    formik.errors.subDirectoryId
                  }
                  sx={{
                    width: "100%",
                    pr: 5,
                    "&:hover": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "black !important",
                      },
                    },
                  }}
                >
                  {subdirectories?.data?.map((subdirectory) => (
                    <MenuItem key={subdirectory.id} value={subdirectory.id}>
                      {" "}
                      {/* Ensure this is the DB ID */}
                      {subdirectory.subdirectoryName}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              <Grid item xs={6}>
                <SelectSubmissionTypes
                  label="Submission Type"
                  placeholder="Select Submission Type"
                  name="submissionTypeId"
                  value={formik.values.submissionTypeId} // Bind Formik value
                  onChange={(fieldName, newValue) => {
                    // Manually update Formik's field value
                    formik.setFieldValue(fieldName, newValue);
                  }}
                  onBlur={() =>
                    formik.setFieldTouched("submissionTypeId", true)
                  } // Mark field as touched
                  errorFormik={
                    formik.touched.submissionTypeId &&
                    Boolean(formik.errors.submissionTypeId)
                  }
                  helperText={
                    formik.touched.submissionTypeId &&
                    formik.errors.submissionTypeId
                  }
                  disabled={loading}
                  sx={{ width: "100%" }}
                />
              </Grid>

              {formik.values.submissionTypeId === 1 && (
                <Grid item xs={12}>
                  <TextField
                    id="linkProvided"
                    label="Insert link"
                    variant="outlined"
                    size="small"
                    disabled={loading}
                    value={formik.values.linkProvided}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.linkProvided &&
                      Boolean(formik.errors.linkProvided)
                    }
                    helperText={
                      formik.touched.linkProvided && formik.errors.linkProvided
                    }
                    fullWidth
                    multiline
                  />
                </Grid>
              )}

              {/* Share To Section */}
              {/* Dynamic Dropdowns for ShareTo */}

              <Grid item xs={12}>
                <Typography>Recipients :</Typography>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="recipient-type-label">
                    Select Recipient Type
                  </InputLabel>
                  <Select
                    labelId="recipient-type-label"
                    value={selectedRecipientType}
                    onChange={handleRecipientTypeChange}
                    label="Select Recipient Type"
                  >
                    {/* Replace these options with your dynamic data */}
                    <MenuItem value="selected">Selected</MenuItem>
                    <MenuItem value="bulk">Bulk</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {selectedRecipientType === "bulk" && (
                <>
                  {shareTo.map((share, index) => (
                    <Grid
                      item
                      xs={12}
                      key={share?.recipient}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        width: "100%",
                      }}
                    >
                      <SelectBulkRecipients
                        name="shareTo"
                        label="Select Recipient"
                        index={index}
                        value={share?.recipient?.recipient_name || ""}
                        onChange={(fieldName, e) => {
                          console.log(
                            `Selected Recipient for Dropdown ${index}:`,
                            e
                          );
                          handleDropdownChange(index, e);
                        }}
                        disabled={false} // Add your loading state if needed
                        onBlur={() => {}}
                        error={Boolean(shareTo[index])}
                        helperText={
                          Boolean(!shareTo[index]) && "No Selected Recipient"
                        }
                      />
                      <Button
                        onClick={() => {
                          setShareTo((prev) => {
                            const updatedShareTo = [...prev];
                            updatedShareTo.splice(index, 1);
                            return updatedShareTo;
                          });
                        }}
                      >
                        Remove Recipient
                      </Button>
                    </Grid>
                  ))}
                </>
              )}

              {selectedRecipientType === "selected" && (
                <>
                  {selectedRecipients.map((select, index) => (
                    <Grid
                      item
                      xs={12}
                      key={select?.selectedRecipient}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        width: "100%",
                      }}
                    >
                      <SelectSpecificRecipients
                        name="selectedRecipients"
                        label="Select Recipient"
                        index={index}
                        value={select?.selectedRecipient?.officeName || ""}
                        onChange={(fieldName, e) => {
                          console.log(
                            `Selected Recipient for Dropdown ${index}:`,
                            e
                          );
                          handleSelectedRecipientsChange(index, e);
                        }}
                        disabled={false} // Add your loading state if needed
                        onBlur={() => {}}
                        error={Boolean(selectedRecipients[index])}
                        helperText={
                          Boolean(!selectedRecipients[index]) &&
                          "No Selected Recipient"
                        }
                      />
                      <Button
                        onClick={() => {
                          setSelectedRecipients((prev) => {
                            const updatedselectedRecipient = [...prev];
                            updatedselectedRecipient.splice(index, 1);
                            return updatedselectedRecipient;
                          });
                        }}
                      >
                        Remove Recipient
                      </Button>
                    </Grid>
                  ))}
                </>
              )}

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddDropdown}
                  disabled={loading}
                >
                  Add Recipient
                </Button>
              </Grid>
            </Grid>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              p: 5,
              zIndex: 10,
            }}
          >
            <Button
              disabled={!(formik.isValid && formik.dirty)} // Enable button only if form is valid and at least one field is filled
              type="submit"
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor:
                  formik.isValid && formik.dirty ? "blue" : "#c3c3c3",
                color: "#fff",
                pt: 1,
                width: "10vw",
                minWidth: "100px",
                "&:hover": {
                  border: "solid 1px gray",
                  backgroundColor:
                    formik.isValid && formik.dirty ? "lightblue" : "lightgray",
                  color: "#2b2b2b",
                  fontWeight: "bold",
                },
              }}
            >
              <AddIcon
                sx={{
                  mr: 1,
                  color: "#fff",
                }}
              />
              Submit
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}
